import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'operador';
  user_type?: 'admin' | 'inscrito' | 'apoio';
  cpf?: string;
  birth_date?: string;
}

interface RegisterData {
  name: string;
  cpf: string;
  email: string;
  password: string;
  birth_date: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  isAdmin: boolean;
  isInscrito: boolean;
  isApoio: boolean;
  canManageRegistrations: boolean;
  registrationData: RegisterData | null;
  setRegistrationData: (data: RegisterData | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [registrationData, setRegistrationData] = useState<RegisterData | null>(null);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const userData = response.data.user;
      setUser(userData);
      localStorage.setItem('token', response.data.token);
      return { success: true };
    } catch (error: any) {
      const backendMessage = 
        error?.response?.data?.errors?.email?.[0] ||
        error?.response?.data?.message ||
        'Email ou senha inválidos';
      return { success: false, message: backendMessage };
    }
  };

  const register = async (data: RegisterData): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post('/auth/register', {
        name: data.name,
        cpf: data.cpf,
        email: data.email,
        password: data.password,
        password_confirmation: data.password, // Adicionar esta linha
        birth_date: data.birth_date,
        user_type: 'inscrito'
      });
      
      // Salvar dados para usar no formulário MCMV
      setRegistrationData(data);
      
      // Fazer login automático após registro
      const loginResult = await login(data.email, data.password);
      
      return { success: loginResult.success };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erro ao criar conta' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setRegistrationData(null);
    localStorage.removeItem('token');
  };

  const isAdmin = user?.role === 'admin' || user?.user_type === 'admin';
  const isInscrito = user?.user_type === 'inscrito';
  const isApoio = user?.user_type === 'apoio';
  const canManageRegistrations = user?.user_type === 'admin' || user?.user_type === 'apoio';

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      isAdmin, 
      isInscrito,
      isApoio,
      canManageRegistrations,
      registrationData, 
      setRegistrationData 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};